import { inject, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';
import { API_BASE_URL, removeUndefinedFields } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class BaseHttpService {
  apiBaseUrl = inject(API_BASE_URL);

  http = inject(HttpClient);

  post<T>(
    path: string,
    body: any,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      params?:
        | HttpParams
        | {
            [param: string]: string | number | boolean;
          };
    },
    urlBaseOverride?: string,
  ) {
    return this.http
      .post<T>(`${urlBaseOverride ?? this.apiBaseUrl}${path}`, body, options)
      .pipe(shareReplay(1));
  }

  get<T>(
    path: string,
    obj?: {
      queryParams?: any;
      params?: HttpParams;
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
    },
    urlBaseOverride?: string,
  ) {
    if (obj?.params) {
      removeUndefinedFields(obj?.queryParams);
    }
    const params = obj?.params
      ? obj.params
      : new HttpParams({ fromObject: obj?.queryParams });
    return this.http
      .get<T>(`${urlBaseOverride ?? this.apiBaseUrl}${path}`, {
        params,
        headers: obj?.headers,
      })
      .pipe(shareReplay(1));
  }

  put<T>(
    path: string,
    id?: number | null,
    body?: any,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
    },
    urlBaseOverride?: string,
  ) {
    const idPath = id ? '/' + id : '';
    return this.http.put<T>(
      `${urlBaseOverride ?? this.apiBaseUrl}${path}${idPath}`,
      body,
      options,
    );
  }
  patch<T>(path: string, id: number, body?: any) {
    return this.http.patch<T>(`${this.apiBaseUrl}${path}/${id}`, body);
  }

  delete<T>(path: string, id: number) {
    return this.http.delete<T>(`${this.apiBaseUrl}${path}/${id}`);
  }

  getBlob(
    path: string,
    obj?: {
      queryParams?: any;
      params?: HttpParams;
      headers?: HttpHeaders | Record<string, string | string[]>;
    },
    urlBaseOverride?: string,
  ): Observable<Blob> {
    const params =
      obj?.params ?? new HttpParams({ fromObject: obj?.queryParams ?? {} });
    return this.http
      .get(`${urlBaseOverride ?? this.apiBaseUrl}${path}`, {
        params,
        headers: obj?.headers,
        responseType: 'blob',
      })
      .pipe(shareReplay(1));
  }

  getBlobResponse(
    path: string,
    obj?: {
      queryParams?: any;
      params?: HttpParams;
      headers?: HttpHeaders | Record<string, string | string[]>;
    },
    urlBaseOverride?: string,
  ): Observable<HttpResponse<Blob>> {
    const params =
      obj?.params ?? new HttpParams({ fromObject: obj?.queryParams ?? {} });
    return this.http.get(`${urlBaseOverride ?? this.apiBaseUrl}${path}`, {
      params,
      headers: obj?.headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  postBlob(
    path: string,
    body: any,
    options?: {
      headers?: HttpHeaders | Record<string, string | string[]>;
    },
    urlBaseOverride?: string,
  ): Observable<Blob> {
    return this.http
      .post(`${urlBaseOverride ?? this.apiBaseUrl}${path}`, body, {
        headers: options?.headers,
        responseType: 'blob',
      })
      .pipe(shareReplay(1));
  }
}
